import React from 'react';
import { useReducedMotion } from '../hooks';
import { useRipples, RippleLayer } from './useRipples';
import './worlds.css';

/**
 * CalmPond — a still-water scene for easing anxiety. The surface shimmers slowly;
 * tap (or press Enter) anywhere to send out a soft, expanding ripple, and a couple
 * of leaves drift gently across. Deliberately unhurried, nothing to finish. Under
 * reduced motion the drift/shimmer stop but taps still ripple.
 */
const LEAVES = [
  { top: '30%', size: 26, dur: '26s', delay: '-4s' },
  { top: '58%', size: 20, dur: '32s', delay: '-14s' },
  { top: '74%', size: 30, dur: '38s', delay: '-22s' },
];

export const CalmPond: React.FC = () => {
  const reduced = useReducedMotion();
  const { ripples, spawnAt, spawnAtElement } = useRipples();

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => spawnAt(e.currentTarget, e.clientX, e.clientY);
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      spawnAtElement(e.currentTarget);
    }
  };

  return (
    <div
      className={`mm-world mm-world--pond ${reduced ? 'is-still' : ''}`}
      role="img"
      aria-label="Calm pond — tap for a gentle ripple"
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <span className="mm-pond__shimmer" aria-hidden="true" />
      <span className="mm-pond__shimmer mm-pond__shimmer--2" aria-hidden="true" />
      {LEAVES.map((l, i) => (
        <span
          key={i}
          className="mm-pond__leaf"
          aria-hidden="true"
          style={{ top: l.top, width: l.size, height: l.size * 0.66, animationDuration: l.dur, animationDelay: l.delay }}
        />
      ))}
      <RippleLayer ripples={ripples} />
      <p className="mm-world__hint">Tap the water for a ripple</p>
    </div>
  );
};

export default CalmPond;
